/**
 * One picker for "who" (t117u): people, departments and groups (optionally buildings and positions, for
 * audiences such as announcements) searched together, results grouped and labelled with icons, selections as chips.
 * Team chips show a member count with the current members in a hover bubble. The value keeps separate id lists, so
 * the data model and APIs that already store people and teams separately are unchanged.
 */
import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Building2, BadgeCheck, Check, Search, X } from 'lucide-react';
import type { Team } from '@tradeoutlet/shared';
import { useMembers, usePositions, useTeams } from '@/lib/hooks';
import { useBuildings } from '@/features/buildings/common';
import { Avatar, HoverBubble } from '@/components/ui';
import { TeamIcon } from './TeamPicker';

const peopleCount = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

export type AudienceKind = 'person' | 'department' | 'group' | 'building' | 'position';
export interface AudienceValue { people: string[]; teams: string[]; buildings?: string[]; positions?: string[] }

interface Option { kind: AudienceKind; id: string; name: string; sub?: string; search: string; team?: Team }
const SECTION: Record<AudienceKind, string> = { person: 'People', department: 'Departments', group: 'Groups', building: 'Buildings', position: 'Positions' };
const ORDER: AudienceKind[] = ['person', 'department', 'group', 'building', 'position'];
const SUGGEST = 4;

const listOf = (v: AudienceValue, k: AudienceKind): string[] =>
  k === 'person' ? v.people : k === 'department' || k === 'group' ? v.teams : k === 'building' ? v.buildings ?? [] : v.positions ?? [];
function withList(v: AudienceValue, k: AudienceKind, ids: string[]): AudienceValue {
  if (k === 'person') return { ...v, people: ids };
  if (k === 'department' || k === 'group') return { ...v, teams: ids };
  return k === 'building' ? { ...v, buildings: ids } : { ...v, positions: ids };
}

/** Everyone a set of people + teams reaches, each person once (a person also in a picked team isn't counted twice). */
export function reachOf(v: Pick<AudienceValue, 'people' | 'teams'>, teamsById: Map<string, Team>): Set<string> {
  const out = new Set(v.people);
  for (const t of v.teams) for (const m of teamsById.get(t)?.members ?? []) out.add(m.user_id);
  return out;
}

/** t704u: a choice above the search results that stands for a whole group, e.g. "Anyone working at the building" or "All buildings". */
export interface AudienceSpecial { id: string; name: string; sub?: string; icon?: ReactNode }

export function AudiencePicker({ value, onChange, kinds = ['person', 'department', 'group'], placeholder = 'Search people, departments and groups', label = 'Add people or teams', helper, disabled, allow, suggest = true, testId = 'audience-picker',
  specials = [], special = null, onSpecial }: {
  value: AudienceValue; onChange: (v: AudienceValue) => void; kinds?: AudienceKind[]; placeholder?: string; label?: string; helper?: ReactNode; disabled?: boolean;
  /** t699u: false = nothing is offered until you type (no suggested people). */
  suggest?: boolean; testId?: string;
  /** Options you may pick (e.g. only buildings and teams you can post to); others aren't offered. Chips already chosen stay. */
  allow?: (kind: AudienceKind, id: string) => boolean;
  /** t704u: special choices listed first (always, not only when searching); the chosen one shows as a chip. The parent keeps them exclusive. */
  specials?: AudienceSpecial[]; special?: string | null; onSpecial?: (id: string | null) => void;
}) {
  const { members, byId } = useMembers();
  const { teams, byId: teamsById } = useTeams();
  const bq = useBuildings().data; const pq = usePositions().data;
  const buildings = useMemo(() => bq?.buildings ?? [], [bq]);
  const positions = useMemo(() => pq?.positions ?? [], [pq]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  const options = useMemo(() => {
    const o: Option[] = [];
    if (kinds.includes('person')) for (const m of members) if (m.status === 'active') o.push({ kind: 'person', id: m.user_id, name: m.display_name, sub: m.job_title ?? undefined, search: `${m.display_name} ${m.email ?? ''} ${m.job_title ?? ''}` });
    for (const t of teams) {
      const k: AudienceKind = t.kind === 'group' ? 'group' : 'department';
      if (kinds.includes(k)) o.push({ kind: k, id: t.id, name: t.name, sub: peopleCount(t.members?.length ?? t.member_count ?? 0), search: t.name, team: t });
    }
    if (kinds.includes('building')) for (const b of buildings) o.push({ kind: 'building', id: b.id, name: b.name, search: `${b.name} ${b.code ?? ''}` });
    if (kinds.includes('position')) for (const p of positions as { id: string; name: string; archived_at?: string | null }[]) if (!p.archived_at) o.push({ kind: 'position', id: p.id, name: p.name, search: p.name });
    return o;
  }, [kinds, members, teams, buildings, positions]);

  const term = q.trim().toLowerCase();
  const groups = useMemo(() => ORDER.filter(k => kinds.includes(k)).map(k => {
    const all = options.filter(o => o.kind === k && !listOf(value, k).includes(o.id) && (!allow || allow(o.kind, o.id)) && (!term || o.search.toLowerCase().includes(term)));
    return { kind: k, items: term ? all.slice(0, 8) : suggest ? all.slice(0, SUGGEST) : [] };
  }).filter(g => g.items.length), [options, kinds, value, term, allow, suggest]);
  const flat = groups.flatMap(g => g.items);
  const sp = specials.filter(x => x.id !== special && (!term || x.name.toLowerCase().includes(term)));
  const total = sp.length + flat.length;
  const at = Math.min(hi, total - 1);
  const activeSpecial = at >= 0 && at < sp.length ? sp[at] : null;
  const active = at >= sp.length ? flat[at - sp.length] : undefined;
  const chosenSpecial = special ? specials.find(x => x.id === special) ?? null : null;

  const add = (o: Option) => { onChange(withList(value, o.kind, [...listOf(value, o.kind), o.id])); setQ(''); setHi(0); input.current?.focus(); };
  const pickSpecial = (x: AudienceSpecial) => { onSpecial?.(x.id); setQ(''); setHi(0); setOpen(false); };
  const remove = (k: AudienceKind, id: string) => onChange(withList(value, k, listOf(value, k).filter(x => x !== id)));
  const chips: { k: AudienceKind; id: string }[] = [
    ...value.teams.map(id => ({ k: (teamsById.get(id)?.kind === 'group' ? 'group' : 'department') as AudienceKind, id })),
    ...(value.buildings ?? []).map(id => ({ k: 'building' as const, id })), ...(value.positions ?? []).map(id => ({ k: 'position' as const, id })),
    ...value.people.map(id => ({ k: 'person' as const, id })),
  ];
  const covered = useMemo(() => { const s = new Set<string>(); for (const t of value.teams) for (const m of teamsById.get(t)?.members ?? []) s.add(m.user_id); return s; }, [value.teams, teamsById]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, total - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (open && activeSpecial) { e.preventDefault(); pickSpecial(activeSpecial); } else if (open && active) { e.preventDefault(); add(active); } }
    else if (e.key === 'Escape') { if (open) { e.stopPropagation(); setOpen(false); } }
    else if (e.key === 'Backspace' && !q && chips.length) { const last = chips[chips.length - 1]; remove(last.k, last.id); }
    else if (e.key === 'Backspace' && !q && chosenSpecial) onSpecial?.(null);
  };

  const icon = (o: { kind: AudienceKind; team?: Team }, cls = 'h-3.5 w-3.5') => o.kind === 'building' ? <Building2 className={cls} /> : o.kind === 'position' ? <BadgeCheck className={cls} /> : <TeamIcon kind={o.kind === 'group' ? 'group' : 'department'} className={cls} />;
  const chipCls = 'inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium';
  return (
    <div className="space-y-2" data-testid={testId}>
      {(chips.length > 0 || chosenSpecial) && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Selected">
          {chosenSpecial && (
            <li data-testid="audience-chip" data-kind="special">
              <span className={clsx(chipCls, 'border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100')}>
                {chosenSpecial.icon}<span className="truncate">{chosenSpecial.name}</span>
                {!disabled && <button type="button" aria-label={`Remove ${chosenSpecial.name}`} onClick={() => onSpecial?.(null)} className="ml-0.5 rounded text-slate-400 hover:text-slate-900 dark:hover:text-white"><X className="h-3 w-3" /></button>}
              </span>
            </li>
          )}
          {chips.map(({ k, id }) => {
            if (k === 'person') {
              const m = byId.get(id); const dup = covered.has(id);
              return (
                <li key={`p-${id}`} data-testid="audience-chip" data-kind="person">
                  <HoverBubble bubble={dup ? 'Also in a selected department or group, so only counted once' : m?.job_title}>
                    <span className={clsx(chipCls, 'border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100')}>
                      <Avatar size={16} name={m?.display_name ?? '?'} id={id} /><span className="truncate">{m?.display_name ?? 'Someone'}</span>
                      {!disabled && <button type="button" aria-label={`Remove ${m?.display_name ?? 'person'}`} onClick={() => remove(k, id)} className="ml-0.5 rounded text-slate-400 hover:text-slate-900 dark:hover:text-white"><X className="h-3 w-3" /></button>}
                    </span>
                  </HoverBubble>
                </li>
              );
            }
            const t = teamsById.get(id);
            const name = t?.name ?? buildings.find(b => b.id === id)?.name ?? (positions as { id: string; name: string }[]).find(p => p.id === id)?.name ?? 'Unknown';
            const people = t?.members ?? [];
            return (
              <li key={`${k}-${id}`} data-testid="audience-chip" data-kind={k}>
                <HoverBubble bubble={t ? (people.length ? <><span className="font-semibold">{SECTION[k].replace(/s$/, '')} · {people.length} {people.length === 1 ? 'person' : 'people'}</span><br />{people.map(p => p.display_name).join(', ')}</> : 'Nobody in it yet') : SECTION[k].replace(/s$/, '')}>
                  <span tabIndex={disabled ? 0 : -1} className={clsx(chipCls, 'border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100')}>
                    {icon({ kind: k })}<span className="truncate">{name}</span>{t && <span className="font-normal text-slate-500 dark:text-slate-400">· {people.length}</span>}
                    {!disabled && <button type="button" aria-label={`Remove ${name}`} onClick={() => remove(k, id)} className="ml-0.5 rounded text-slate-400 hover:text-slate-900 dark:hover:text-white"><X className="h-3 w-3" /></button>}
                  </span>
                </HoverBubble>
              </li>
            );
          })}
        </ul>
      )}
      {!disabled && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input ref={input} value={q} onChange={e => { setQ(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={onKey}
            role="combobox" aria-expanded={open && total > 0} aria-controls={listId} aria-autocomplete="list" aria-label={label}
            aria-activedescendant={open && activeSpecial ? `${listId}-special-${activeSpecial.id}` : open && active ? `${listId}-${active.kind}-${active.id}` : undefined} placeholder={placeholder}
            className="h-9 w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 text-sm outline-none focus:border-slate-500 dark:border-slate-600 dark:bg-slate-900" data-testid="audience-search" />
          {open && total > 0 && (
            <ul id={listId} role="listbox" className="absolute z-40 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
              {sp.map((x, i) => (
                <li key={`special-${x.id}`} id={`${listId}-special-${x.id}`} role="option" aria-selected={false} data-testid="audience-option" data-kind="special"
                  onMouseDown={e => e.preventDefault()} onClick={() => pickSpecial(x)} onMouseEnter={() => setHi(i)}
                  className={clsx('flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm', i === at && 'bg-slate-100 dark:bg-slate-800')}>
                  <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">{x.icon}</span>
                  <span className="min-w-0 flex-1 truncate">{x.name}</span>
                  {x.sub && <span className="shrink-0 text-xs text-slate-500">{x.sub}</span>}
                </li>
              ))}
              {groups.map(g => (
                <li key={g.kind} role="presentation">
                  <div className="px-2 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{SECTION[g.kind]}</div>
                  <ul role="group" aria-label={SECTION[g.kind]}>
                    {g.items.map(o => {
                      const i = sp.length + flat.indexOf(o);
                      return (
                        <li key={o.id} id={`${listId}-${o.kind}-${o.id}`} role="option" aria-selected={false} data-testid="audience-option" data-kind={o.kind}
                          onMouseDown={e => e.preventDefault()} onClick={() => add(o)} onMouseEnter={() => setHi(i)}
                          className={clsx('flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm', i === at && 'bg-slate-100 dark:bg-slate-800')}>
                          {o.kind === 'person' ? <Avatar size={22} name={o.name} id={o.id} /> : <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">{icon(o)}</span>}
                          <span className="min-w-0 flex-1 truncate">{o.name}</span>
                          {o.sub && <span className="shrink-0 text-xs text-slate-500">{o.sub}</span>}
                          {i === at && <Check className="h-3.5 w-3.5 text-slate-400" aria-hidden />}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {helper && <p className="text-xs text-slate-500">{helper}</p>}
    </div>
  );
}

/** Tasks (t117u): people + departments/groups. Adding a team assigns it to whoever is in it now and later. */
export function AssigneePicker({ assigneeIds, teamIds, onChange, disabled }: { assigneeIds: string[]; teamIds: string[]; onChange: (v: { assignee_ids: string[]; team_ids: string[] }) => void; disabled?: boolean }) {
  const { byId: teamsById } = useTeams();
  const n = reachOf({ people: assigneeIds, teams: teamIds }, teamsById).size;
  return (
    <AudiencePicker disabled={disabled} value={{ people: assigneeIds, teams: teamIds }} onChange={v => onChange({ assignee_ids: v.people, team_ids: v.teams })}
      placeholder="Search people, departments and groups"
      helper={<span data-testid="assignee-reach">{teamIds.length ? `${n} ${n === 1 ? 'person' : 'people'}, including anyone who joins a picked team later.` : n ? `${n} ${n === 1 ? 'person' : 'people'}` : 'Add people, or a team to assign everyone in it.'}</span>} label="Add assignees" />
  );
}
